import type { ComponentProps } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom-v5';
import { PanelLeft } from 'lucide-react';
import Logo from '../../components/library/images/logo.svg';
import LogoSmall from '../../components/library/images/logo-small.svg';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarSeparator,
  useSidebar,
} from '@/shared/ui/sidebar';
import { API_URL } from '@shared/env';
import { getAllClassrooms } from '../../selectors/classroomSelectors';
import { getAllCompanies } from '../../selectors/companySelectors';
import {
  canAccessDownloads,
  canAccessMockList,
  hasMockCourses as hasMockCoursesSelector,
} from '../../selectors/courseSelectors';
import { NavItem } from './NavItem';
import { NotificationsNavItem } from './NotificationsNavItem';
import { UserMenu } from './UserMenu';
import {
  resolveSections,
  type NavigationContext,
} from './navigationConfig';
import { useCurrentUser } from './useCurrentUser';

const sidebarToggleStyle: React.CSSProperties = {
  width: 28,
  height: 28,
  border: '1px solid #E4E4E7',
  borderRadius: 8,
};

const headerCollapsedWrapperStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '10px 8px',
  gap: 8,
  borderBottom: '1px solid #E2E8F0',
};

const headerExpandedWrapperStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px',
  borderBottom: '1px solid #E2E8F0',
};

const headerLogoWrapperStyle: React.CSSProperties = { padding: '10px' };

const collapsedLogoStyle: React.CSSProperties = { height: 22, width: 18 };
const expandedLogoStyle: React.CSSProperties = { height: 22 };

const sidebarHeaderStyle: React.CSSProperties = { padding: 0, gap: 0 };

function SidebarToggle() {
  const { state, toggleSidebar } = useSidebar();
  const isCollapsed = state === 'collapsed';

  return (
    <button
      type="button"
      onClick={toggleSidebar}
      style={sidebarToggleStyle}
      className="flex shrink-0 items-center justify-center hover:bg-[#E2E8F0] transition-colors cursor-pointer"
      aria-label={isCollapsed ? 'Expandir menu' : 'Recolher menu'}
    >
      <PanelLeft className="size-4" />
    </button>
  );
}

export function AppSidebar() {
  const user = useCurrentUser();
  const { state, isMobile } = useSidebar();
  // The mobile Sheet always shows the full offcanvas layout — the
  // desktop `state` (driven by the persisted sidebar_state cookie) is
  // irrelevant there. Without this guard, a user that collapsed the
  // desktop sidebar would see the collapsed-mode markup (small logo,
  // per-section separators, icon-only UserMenu) rendered inside the
  // mobile Sheet.
  const isCollapsed = !isMobile && state === 'collapsed';

  const hasMockCourses = useSelector((s: unknown) =>
    hasMockCoursesSelector(s as never),
  ) as boolean;
  const canAccessMockListValue = useSelector((s: unknown) =>
    canAccessMockList(s as never),
  ) as boolean;
  const canAccessDownloadsValue = useSelector((s: unknown) =>
    canAccessDownloads(s as never),
  ) as NavigationContext['canAccessDownloads'];
  const classrooms = useSelector((s: unknown) =>
    getAllClassrooms(s as never),
  ) as Array<unknown>;
  const companies = useSelector((s: unknown) =>
    getAllCompanies(s as never),
  ) as NavigationContext['companies'];

  const ctx: NavigationContext = {
    user,
    hasMockCourses: !!hasMockCourses,
    canAccessMockList: !!canAccessMockListValue,
    canAccessDownloads: canAccessDownloadsValue,
    classroomsCount: Array.isArray(classrooms) ? classrooms.length : 0,
    companies: companies ?? [],
    apiUrl: API_URL,
  };

  const sections = resolveSections(ctx);

  return (
    <Sidebar collapsible="icon" className="border-r">
      <SidebarHeader style={sidebarHeaderStyle}>
        {isCollapsed ? (
          <div style={headerCollapsedWrapperStyle}>
            <div style={headerLogoWrapperStyle}>
              <Link to="/" className="flex items-center">
                <img src={LogoSmall} alt="T2 Educação" style={collapsedLogoStyle} />
              </Link>
            </div>
            <SidebarToggle />
          </div>
        ) : (
          <div style={headerExpandedWrapperStyle}>
            <div style={headerLogoWrapperStyle}>
              <Link to="/" className="flex items-center">
                <img src={Logo} alt="T2 Educação" style={expandedLogoStyle} />
              </Link>
            </div>
            <SidebarToggle />
          </div>
        )}
      </SidebarHeader>

      <SidebarContent>
        {sections.map((section) => (
          <div key={section.key}>
            {section.separatorBefore && isCollapsed && <SidebarSeparator />}
            <SidebarGroup>
            <SidebarGroupLabel>{section.label}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {section.items.map((item) => {
                  if (item.key === 'notifications') {
                    return (
                      <NotificationsNavItem
                        key={item.key}
                        label={item.label}
                      />
                    );
                  }
                  return (
                    <NavItem
                      key={item.key}
                      item={item as ComponentProps<typeof NavItem>['item']}
                      ctx={ctx}
                    />
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
          </div>
        ))}
      </SidebarContent>

      <SidebarFooter className="p-0">
        <UserMenu />
      </SidebarFooter>
    </Sidebar>
  );
}
